import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectForm } from '../context/ProjectFormContext';
import { submitProjectRequest } from '../services/projectSubmission';
import { FiArrowRight, FiAlertCircle } from 'react-icons/fi';

/**
 * ProjectRequestForm
 * Embedded form for contact page - single step form
 */
const ProjectRequestForm = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, setLeadId } = useProjectForm();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'error' only
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!agreedToPrivacy) {
      newErrors.privacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await submitProjectRequest(formData);
      
      if (result.success) {
        if (result.d1?.id) setLeadId(result.d1.id);
        navigate('/survey');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };



  if (submitStatus === 'error') {
    return (
      <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
        <div className="flex items-start gap-4">
          <FiAlertCircle size={32} className="text-red-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-2xl font-bold text-red-900 mb-3">
              Something Went Wrong
            </h3>
            <p className="text-red-800 mb-4">
              We couldn't process your request. Please check your information and try again.
            </p>
            <button
              onClick={() => {
                setSubmitStatus(null);
              }}
              className="mt-6 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 md:p-8 ">      
    <div class="absolute -left-12 -top-12 w-32 h-32 rounded-full bg-[#d6fa70]/40 blur-3xl" aria-hidden="true"></div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => {
              updateFormData('name', e.target.value);
            }}
            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
            placeholder="Your name"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Email *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => {
              updateFormData('email', e.target.value);
              if (errors.email) setErrors({ ...errors, email: '' });
            }}
            className={`w-full px-4 py-2 rounded-lg border ${
              errors.email ? 'border-red-500' : 'border-slate-200'
            } bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900`}
            placeholder="your@email.com"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Company
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => {
              updateFormData('company', e.target.value);
            }}
            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900"
            placeholder="Company name"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Message
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => {
              updateFormData('message', e.target.value);
            }}
            rows="4"
            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
            placeholder="Tell us about your project..."
          />
        </div>

        {/* Privacy Policy Checkbox */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="privacy-policy"
            checked={agreedToPrivacy}
            onChange={(e) => {
              setAgreedToPrivacy(e.target.checked);
              if (errors.privacy) setErrors({ ...errors, privacy: '' });
            }}
            className="w-5 h-5 rounded border-slate-300 mt-0.5 accent-slate-900 cursor-pointer"
          />
          <label htmlFor="privacy-policy" className="text-sm text-slate-600 cursor-pointer">
            By sending this request, you agree to our{' '}
            <a href="/privacy" className="font-semibold text-slate-900 hover:underline">
              Privacy Policy
            </a>
            .
          </label>
        </div>
        {errors.privacy && <p className="text-red-500 text-xs mt-1">{errors.privacy}</p>}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className={`w-full px-6 py-3 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
            submitting
              ? 'bg-slate-400 text-white cursor-not-allowed'
              : 'bg-[#d6fa70] text-slate-900 hover:bg-[#c9f050]'
          }`}
        >
          {submitting ? 'Sending...' : 'Submit'}
          {!submitting && <FiArrowRight size={18} />}
        </button>

        <p className="text-xs text-slate-500">
          * Required fields.
        </p>
      </form>
    </div>
  );
};

export default ProjectRequestForm;
